import { logErrorRemotely } from './remote-error-logger';
import { systemLogger } from './logging';

/**
 * Global error handler that catches all uncaught exceptions and unhandled promise rejections
 * This ensures that all errors are logged to the remote error logging system
 */
export function setupGlobalErrorHandler() {
  systemLogger.info('Setting up global error handlers');

  // 1. Catch uncaught exceptions
  window.addEventListener('error', (event) => {
    systemLogger.error('Uncaught error', event.error);

    logErrorRemotely({
      errorType: 'unknown_exception',
      errorMessage: event.error?.message || event.message || 'Unknown error',
      errorStack: event.error?.stack,
      operationName: 'global-uncaught',
      operationPayload: {
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno
      }
    }).catch((_error) => {
      // Silent fail - don't break app if error logging fails
      systemLogger.warn('Failed to log uncaught error remotely');
    });

    // Don't prevent default error handling
    return false;
  });

  // 2. Catch unhandled promise rejections
  window.addEventListener('unhandledrejection', (event) => {
    systemLogger.error('Unhandled promise rejection', event.reason);

    const error = event.reason;
    logErrorRemotely({
      errorType: 'unknown_exception',
      errorMessage: error?.message || String(error) || 'Unhandled promise rejection',
      errorStack: error?.stack,
      operationName: 'global-unhandled-rejection'
    }).catch((_error) => {
      // Silent fail - don't break app if error logging fails
      systemLogger.warn('Failed to log unhandled rejection remotely');
    });

    // Don't prevent default error handling
    return false;
  });

  // 3. Intercept console.error to catch explicitly logged errors
  const originalConsoleError = console.error; // eslint-disable-line no-console
  console.error = function(...args: unknown[]) { // eslint-disable-line no-console
    // Call original console.error first
    originalConsoleError.apply(console, args);

    // Extract error if first arg is Error object
    const error = args[0] instanceof Error ? args[0] : null;
    if (error) {
      logErrorRemotely({
        errorType: 'console_error',
        errorMessage: error.message || 'Console error',
        errorStack: error.stack,
        operationName: 'console-error',
        operationPayload: {
          additionalArgs: args.slice(1).map(arg => String(arg)).join(', ')
        }
      }).catch((_error) => {
        // Silent fail - don't break app if error logging fails
        // Use original console.error to avoid infinite loop
        originalConsoleError.call(console, 'Failed to log console error remotely');
      });
    }
  };

  systemLogger.info('Global error handlers configured successfully');
}
